import { useState } from "react";
import toast from "react-hot-toast";

import { PaymentDetailIcon } from "@/components/icons";
import SlideOver from "@/components/SlideOver";
import PaymentReceipt, { PaymentDetails } from "./payment-details-receipt";
import {
    downloadPaymentDocument,
    reuseControlNumber,
} from "@/features/applications/services/applications-services";

interface IPaymentDetailsSlideOverProps {
    setShow: () => void
    show: boolean
    /** Control number this bill was issued under — enables print & reuse. */
    controlNumber?: string
    /** Called after a successful reuse so the parent can refetch the bill. */
    onReused?: () => void
    paymentData?: {
        customerName?: string;
        projectName?: string;
        referenceNumber?: string;
        paymentStatus?: string;
        amountPaid?: string | number;
        paymentMethod?: string;
        phoneNumber?: string;
        referenceId?: string;
        date?: string;
        generatedDate?: string;
        expiryDate?: string;
        billedItem?: string;
        amountInWords?: string;
        preparedBy?: string;
    }
}

// A bill counts as paid when its status reads paid/success/completed — the
// receipt download and the "paid" badge key off this.
function isPaidStatus(status?: string): boolean {
    return !!status && /paid|success|complete/i.test(status);
}

export default function PaymentDetailsSlideOver({ setShow, show, controlNumber, onReused, paymentData }: IPaymentDetailsSlideOverProps) {
    const [isReusing, setIsReusing] = useState(false);

    // Neutral placeholder shown when a real field is absent — never fabricate
    // customer names, amounts, or references.
    const EMPTY = "—";

    // Pass real payment values straight through; where a field is missing show a
    // neutral placeholder rather than dummy data.
    const finalPaymentData: PaymentDetails = {
        customerName: paymentData?.customerName || EMPTY,
        projectName: paymentData?.projectName || EMPTY,
        referenceNumber: paymentData?.referenceNumber || EMPTY,
        paymentStatus: paymentData?.paymentStatus || "Pending",
        amountPaid: paymentData?.amountPaid != null && paymentData.amountPaid !== ''
            ? (typeof paymentData.amountPaid === 'number'
                ? paymentData.amountPaid.toLocaleString() + ' TZS'
                : String(paymentData.amountPaid))
            : EMPTY,
        paymentMethod: paymentData?.paymentMethod || EMPTY,
        phoneNumber: paymentData?.phoneNumber || EMPTY,
        referenceId: paymentData?.referenceId || EMPTY,
        date: paymentData?.date || EMPTY,
        generatedDate: paymentData?.generatedDate,
        expiryDate: paymentData?.expiryDate,
        billedItem: paymentData?.billedItem,
        amountInWords: paymentData?.amountInWords,
        preparedBy: paymentData?.preparedBy
    };

    const isPaid = isPaidStatus(paymentData?.paymentStatus);

    const handleDownload = async (type: "invoice" | "receipt") => {
        if (!controlNumber) return;
        try {
            await downloadPaymentDocument(controlNumber, type);
        } catch (e: any) {
            toast.error(e?.message || "Failed to download document");
        }
    };

    const handleReuse = async (reason: string) => {
        if (!controlNumber) return;
        setIsReusing(true);
        try {
            await reuseControlNumber({
                billCntrNum: controlNumber,
                reuseReasn: reason || undefined,
                custName: paymentData?.customerName,
            });
            toast.success("Control number re-activated — you can pay it again.");
            onReused?.();
        } catch (e: any) {
            toast.error(e?.message || "Failed to reuse control number");
        } finally {
            setIsReusing(false);
        }
    };

    return (
        <SlideOver
            title="Payment Details"
            size="sm"
            onClose={setShow}
            isOpen={show}
    >
        <div className="flex justify-center">
            <PaymentDetailIcon />
        </div>
        <div>
            <PaymentReceipt
                payment={finalPaymentData}
                controlNumber={controlNumber}
                isPaid={isPaid}
                onDownloadInvoice={() => handleDownload("invoice")}
                onDownloadReceipt={() => handleDownload("receipt")}
                onReuse={handleReuse}
                isReusing={isReusing}
            />
        </div>
    </SlideOver>
    )
}
